'use client';

import { useCallback, useEffect, useMemo, useState, useSyncExternalStore, type SetStateAction } from 'react';
import { usePathname } from 'next/navigation';
import { readUrlState, writeUrlState, type UrlRules, type UrlValue } from '@/lib/url-state';

const eventName = 'study-url-navigation';
const snapshot = () => window.location.search;
const serverSnapshot = () => '';
let observingHistory = false;
let navigationQueued = false;
function scheduleNavigation() {
  if (navigationQueued) return;
  navigationQueued = true;
  // Next can write history during its insertion effect. The event itself must
  // also leave that commit stack, including for listeners retained by HMR.
  queueMicrotask(() => {
    navigationQueued = false;
    window.dispatchEvent(new Event(eventName));
  });
}
function observeHistory() {
  if (observingHistory) return;
  observingHistory = true;
  // Observe Next's client-side Link/router updates too, including same-route
  // query changes. Native Back/Forward is covered separately by popstate.
  for (const method of ['pushState', 'replaceState'] as const) {
    const original = window.history[method];
    window.history[method] = function (...args: Parameters<History['pushState']>) {
      original.apply(this, args);
      scheduleNavigation();
    };
  }
}
function subscribe(notify: () => void) {
  observeHistory();
  let active = true;
  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    // Next writes browser history inside useInsertionEffect. Notifying React's
    // external store synchronously there schedules an illegal commit-time update.
    // Defer at the subscription boundary (also safe for old history wrappers
    // retained by Fast Refresh) and coalesce writes into the latest snapshot.
    queueMicrotask(() => {
      queued = false;
      if (active) notify();
    });
  };
  window.addEventListener('popstate', schedule);
  window.addEventListener(eventName, schedule);
  return () => {
    active = false;
    window.removeEventListener('popstate', schedule);
    window.removeEventListener(eventName, schedule);
  };
}
let pending: { original: string; url: URL; replace: boolean } | undefined;

export function useUrlState<T extends Record<string, UrlValue>>(defaults: T, rules: UrlRules<T> = {}) {
  // Also re-read when Next navigates without a popstate event.
  const pathname = usePathname();
  const search = useSyncExternalStore(subscribe, snapshot, serverSnapshot);
  const config = JSON.stringify({ defaults, rules });
  const stable = useMemo(() => JSON.parse(config) as { defaults: T; rules: UrlRules<T> }, [config]);
  const state = useMemo(() => readUrlState(search, stable.defaults, stable.rules), [search, stable, pathname]);
  const setState = useCallback((action: SetStateAction<T>, mode: 'push' | 'replace' = 'push') => {
    const original = window.location.href;
    const current = pending?.original === original ? pending.url : new URL(original);
    const previous = readUrlState(current.search, stable.defaults, stable.rules);
    const next = typeof action === 'function' ? action(previous) : action;
    const url = writeUrlState(current, stable.defaults, next);
    if (url.href === current.href) return;
    if (pending?.original === original) {
      pending.url = url;
      pending.replace = pending.replace && mode === 'replace';
      return;
    }
    const transaction = { original, url, replace: mode === 'replace' };
    pending = transaction;
    queueMicrotask(() => {
      if (pending !== transaction) return;
      pending = undefined;
      if (window.location.href !== original || transaction.url.href === original) return;
      window.history[transaction.replace ? 'replaceState' : 'pushState'](window.history.state, '', transaction.url);
    });
  }, [stable]);
  return [state, setState] as const;
}

type Widen<T> = T extends number ? number : T extends boolean ? boolean : T extends string ? string : never;
export function useUrlParam<T extends UrlValue>(key: string, fallback: T, rule = {} as UrlRules<Record<string, T>>[string], mode: 'push' | 'replace' = 'push') {
  const [state, update] = useUrlState({ [key]: fallback }, { [key]: rule });
  const setter = useCallback((value: SetStateAction<Widen<T>>) => update(current => ({ [key]: (typeof value === 'function' ? value(current[key] as unknown as Widen<T>) : value) as unknown as T }), mode), [key, update, mode]);
  return [state[key] as unknown as Widen<T>, setter] as const;
}

/** A restored URL wins over an unfinished search debounce (including Back/Forward). */
export function useUrlSearchDraft(committed: string, commit: (query: string) => void) {
  const navigation = useSyncExternalStore(subscribe, snapshot, serverSnapshot);
  const [draft, setDraft] = useState({ base: committed, value: committed, navigation });
  useEffect(() => { setDraft({base:committed,value:committed,navigation}); }, [committed,navigation]);
  const value = draft.base === committed && draft.navigation === navigation ? draft.value : committed;
  useEffect(() => {
    if (draft.base !== committed || draft.navigation !== navigation || draft.value.trim() === committed) return;
    const timer = window.setTimeout(() => commit(draft.value.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [draft, committed, navigation, commit]);
  return [value, (value: string) => setDraft({ base: committed, value, navigation })] as const;
}

export function useUrlStep() {
  const [index, setIndex] = useUrlParam('step', 0, {max:2});
  return [index + 1, (step: number) => setIndex(step - 1)] as const;
}
